import {
  RewardFareClass,
  FlightsClassTypes,
  fareClassToApiCabinMap,
  isRewardFareClass,
  toBaseFareClass,
  type FareClassWithReward,
} from 'constants/flight'
import CardCarousel from 'LuxKit/Carousel/CardCarousel'
import Modal from 'LuxKit/Modal/Modal'
import { useCallback, useContext, useMemo, useState, useEffect } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import getHasLoyaltyAccount from 'loyalty/selectors/getHasLoyaltyAccount'
import LoyaltyJoinModal from 'loyalty/components/LoyaltyJoinModal'
import enrolLoyaltyAccount from 'actions/loyalty/enrolLoyaltyAccount'
import ModalContext from 'contexts/ModalContext'
import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import { rem } from 'polished'
import styled from 'styled-components'
import FareClassCard from './FareClassCard'
import MaldivesCharterFareClassModalBundleFromPrice from './MaldivesCharterFareClassModalBundleFromPrice'
import MaldivesCharterRewardSeatsToggle from './MaldivesCharterRewardSeatsToggle'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import Group from 'components/utils/Group'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import BodyText from 'LuxKit/Typography/BodyText'
import { useScreenSizeUp } from 'hooks/useScreenSize'
import useCharterFlightJourneysForHotelDates from 'hooks/CharterFlights/useCharterFlightJourneysForHotelDates'
import { useCharterFlightPrice } from 'hooks/useFlightPrice'
import { MALDIVES_CHARTER_ROUTE } from 'api/lib/flightDealMap'
import { CHARTER_CABIN_CONTENT } from 'components/Flights/CharterFlights/charterCabinContent'
import CharterCabinFeatureList from 'components/Flights/CharterFlights/CharterCabinFeatureList'
import { getCharterFareCabinLabel } from 'hooks/Flights/useCharterFlightLabels'
import getLoyaltyProgramDisplayName from 'loyalty/selectors/getLoyaltyProgramDisplayName'

const DEFAULT_OCCUPANCIES: Array<App.Occupants> = []

const CarouselContainer = styled.div`
  padding-top: ${rem(12)};
`

interface Props {
  offer?: App.HotelOffer
  checkInDate?: string
  checkOutDate?: string
  occupancies?: Array<App.Occupants>
  selectedClassId: FareClassWithReward
  allowedFareClasses: ReadonlyArray<FareClassWithReward>
  nights?: number
  luxPlusEnabled?: boolean
  /**
   * Forwarded to /flightsearch so this modal shares feeder-aware cache keys and includes
   * charter feeder estimates when available.
   */
  feederOriginAirportCode?: string
  rewardSeatsOnly?: boolean
  onRewardSeatsOnlyChange?: (enabled: boolean) => void
}

/**
 * Maldives charter fare class selection modal.
 * Renders a card per base cabin with cash and reward options inside; resolves with the
 * user's selection via the modal element context.
 */
function MaldivesCharterFlightFareClassModal({
  offer,
  selectedClassId,
  checkInDate,
  checkOutDate,
  occupancies = DEFAULT_OCCUPANCIES,
  allowedFareClasses,
  nights,
  luxPlusEnabled = false,
  feederOriginAirportCode,
  rewardSeatsOnly: rewardSeatsOnlyProp = false,
  onRewardSeatsOnlyChange,
}: Props) {
  const intl = useIntl()
  const modalElement = useModalElementContext<FareClassWithReward | undefined>()

  const [localSelectedClass, setLocalSelectedClass] = useState<FareClassWithReward>(selectedClassId)
  // Mirror the prop into local state so internal toggle flips are responsive even though the
  // modal element is a snapshot captured at `showModal(...)` JSX-creation time. The parent
  // is informed via `onRewardSeatsOnlyChange` so URL state stays in sync.
  // The modal lives outside `<NuqsAdapter>` (it's portalled above it via ModalProvider), so
  // we can't call `useQueryState` here directly. Callers that need to flip the toggle
  // immediately before opening can pass `rewardSeatsOnly` explicitly — see
  // `useOpenCharterFareClassModal.open({ rewardSeatsOnly })`.
  const [rewardSeatsOnly, setRewardSeatsOnlyLocal] = useState(rewardSeatsOnlyProp)
  const setRewardSeatsOnly = useCallback((enabled: boolean) => {
    setRewardSeatsOnlyLocal(enabled)
    onRewardSeatsOnlyChange?.(enabled)
  }, [onRewardSeatsOnlyChange])
  const isDesktopUp = useScreenSizeUp('desktop')

  // Reward fare classes require an authenticated `customerId` to query availability
  // (svc-lux-loyalty `/rewards/availability`). When a logged-out user picks a reward fare,
  // the modal locks: primary CTA flips to "Log in to view". `lockedForLogin` flips back to
  // `false` once the customer signs in; we watch for that transition to auto-resolve the
  // modal with the customer's reward choice (see effect below).
  const memberId = useAppSelector(state => state.auth.account.memberId)
  const hasLoyaltyAccount = useAppSelector(getHasLoyaltyAccount)
  const showModal = useContext(ModalContext)
  const lockedForLogin = !memberId && isRewardFareClass(localSelectedClass)
  const lockedForEnroll = !!memberId && !hasLoyaltyAccount && isRewardFareClass(localSelectedClass)
  const [didTriggerLogin, setDidTriggerLogin] = useState(false)

  const onClassSelect = useCallback((fareClass: string) => {
    setLocalSelectedClass(fareClass as FareClassWithReward)
  }, [])

  const closeModal = useCallback(() => {
    // Dismissing the modal (click outside / X) should not change the selection.
    // Resolve with undefined so the caller's `if (selectedClass)` check skips updates.
    modalElement.resolve(undefined)
  }, [modalElement])

  const dispatch = useAppDispatch()
  const activateCta = intl.formatMessage({
    id: 'IKjGSqLP_bASWk8A0jWU',
    defaultMessage: 'Activate membership',
  })
  const handlePrimaryActionClick = useCallback(async() => {
    if (lockedForLogin) {
      setDidTriggerLogin(true)
      showModal(<AccountAccessModal />)
      return
    }
    if (lockedForEnroll) {
      const joinConfirmed = await showModal<boolean>(<LoyaltyJoinModal
        onAfterClose={() => {}}
        primaryActionCta={activateCta}
      />)
      if (joinConfirmed) {
        dispatch(enrolLoyaltyAccount({ enrolment: 'existingUserRegistration' }))
      }
      return
    }
    modalElement.resolve(localSelectedClass)
  }, [lockedForLogin, lockedForEnroll, showModal, modalElement, localSelectedClass, activateCta, dispatch])

  // After the customer logs in via the AccountAccessModal we triggered, resolve the fare
  // modal with their reward selection automatically — no second click on "Continue" needed.
  useEffect(() => {
    if (didTriggerLogin && memberId) {
      modalElement.resolve(localSelectedClass)
    }
  }, [didTriggerLogin, memberId, modalElement, localSelectedClass])

  // We only read `upsellFaresPerLeg` (cabin-agnostic), so we pass the modal-open selection
  // rather than `localSelectedClass` to keep the underlying query stable as the user clicks cards.
  const { fareFamilies } = useCharterFlightJourneysForHotelDates({
    charterBundleRoute: MALDIVES_CHARTER_ROUTE,
    checkInDate,
    checkOutDate,
    occupancies,
    fareClass: toBaseFareClass(selectedClassId),
    feederOriginAirportCode,
  })

  const hasDates = !!(checkInDate && checkOutDate)

  const visibleFareClasses = useMemo<ReadonlyArray<FareClassWithReward>>(() => {
    if (rewardSeatsOnly) {
      return allowedFareClasses.filter(fareClass => isRewardFareClass(fareClass))
    }
    return allowedFareClasses
  }, [allowedFareClasses, rewardSeatsOnly])

  const baseFareClasses = useMemo<ReadonlyArray<FlightsClassTypes>>(() => {
    return visibleFareClasses
      .map(toBaseFareClass)
      .filter((baseFareClass, index, baseFareClasses) =>
        baseFareClasses.indexOf(baseFareClass) === index,
      )
  }, [visibleFareClasses])

  /** Reward fare classes only flow into `allowedFareClasses` when the parent has flag-gated their inclusion. */
  const hasAnyRewardFareClass = useMemo(
    () => allowedFareClasses.some(fareClass => isRewardFareClass(fareClass)),
    [allowedFareClasses],
  )

  /**
   * Reward fare-class availability is a separate svc-lux-loyalty lookup per cabin.
   * Calling `useCharterFlightPrice` here is free at runtime — React Query and the redux
   * flight-search cache dedup against the per-card fetches downstream.
   */
  const economyRewardPrice = useCharterFlightPrice({
    charterBundleRoute: MALDIVES_CHARTER_ROUTE,
    checkInDate,
    checkOutDate,
    occupancies,
    fareClass: RewardFareClass.ECONOMY_REWARDS,
    disabled: !hasDates || !hasLoyaltyAccount || !visibleFareClasses.includes(RewardFareClass.ECONOMY_REWARDS),
    feederOriginAirportCode,
  })
  const premiumRewardPrice = useCharterFlightPrice({
    charterBundleRoute: MALDIVES_CHARTER_ROUTE,
    checkInDate,
    checkOutDate,
    occupancies,
    fareClass: RewardFareClass.PREMIUM_REWARDS,
    disabled: !hasDates || !hasLoyaltyAccount || !visibleFareClasses.includes(RewardFareClass.PREMIUM_REWARDS),
    feederOriginAirportCode,
  })
  const luxRowRewardPrice = useCharterFlightPrice({
    charterBundleRoute: MALDIVES_CHARTER_ROUTE,
    checkInDate,
    checkOutDate,
    occupancies,
    fareClass: RewardFareClass.LUXROW_REWARDS,
    disabled: !hasDates || !hasLoyaltyAccount || !visibleFareClasses.includes(RewardFareClass.LUXROW_REWARDS),
    feederOriginAirportCode,
  })
  const businessRewardPrice = useCharterFlightPrice({
    charterBundleRoute: MALDIVES_CHARTER_ROUTE,
    checkInDate,
    checkOutDate,
    occupancies,
    fareClass: RewardFareClass.BUSINESS_REWARDS,
    disabled: !hasDates || !hasLoyaltyAccount || !visibleFareClasses.includes(RewardFareClass.BUSINESS_REWARDS),
    feederOriginAirportCode,
  })

  const unavailableFareClasses = useMemo<Set<FareClassWithReward>>(() => {
    const unavailable = new Set<FareClassWithReward>()

    if (!hasDates) return unavailable

    if (fareFamilies?.upsellFaresPerLeg) {
      const availableCabins = new Set(
        fareFamilies.upsellFaresPerLeg.flatMap(leg => leg.map(fare => fare.cabin)),
      )
      visibleFareClasses.forEach(fareClass => {
        const baseFareClass = toBaseFareClass(fareClass)
        if (baseFareClass === FlightsClassTypes.ECONOMY) return
        if (!availableCabins.has(fareClassToApiCabinMap[baseFareClass])) {
          unavailable.add(fareClass)
        }
      })
    }

    // Only mark reward classes as unavailable when the user has a loyalty account
    // (i.e. the availability API was actually queried). Non-members see preview pricing.
    if (hasLoyaltyAccount) {
      if (!economyRewardPrice.fetching && !economyRewardPrice.rewardAvailable) {
        unavailable.add(RewardFareClass.ECONOMY_REWARDS)
      }
      if (!premiumRewardPrice.fetching && !premiumRewardPrice.rewardAvailable) {
        unavailable.add(RewardFareClass.PREMIUM_REWARDS)
      }
      if (!luxRowRewardPrice.fetching && !luxRowRewardPrice.rewardAvailable) {
        unavailable.add(RewardFareClass.LUXROW_REWARDS)
      }
      if (!businessRewardPrice.fetching && !businessRewardPrice.rewardAvailable) {
        unavailable.add(RewardFareClass.BUSINESS_REWARDS)
      }
    }

    return unavailable
  }, [
    hasDates,
    hasLoyaltyAccount,
    fareFamilies,
    visibleFareClasses,
    economyRewardPrice.rewardAvailable, economyRewardPrice.fetching,
    premiumRewardPrice.rewardAvailable, premiumRewardPrice.fetching,
    luxRowRewardPrice.rewardAvailable, luxRowRewardPrice.fetching,
    businessRewardPrice.rewardAvailable, businessRewardPrice.fetching,
  ])

  const firstAvailableClass = useMemo(() => {
    return visibleFareClasses.find(fareClass => !unavailableFareClasses.has(fareClass))
  }, [visibleFareClasses, unavailableFareClasses])

  useEffect(() => {
    const selectionMissing = !visibleFareClasses.includes(localSelectedClass)
    const selectionUnavailable = unavailableFareClasses.has(localSelectedClass)
    if ((selectionMissing || selectionUnavailable) && firstAvailableClass) {
      setLocalSelectedClass(firstAvailableClass)
    }
  }, [visibleFareClasses, unavailableFareClasses, localSelectedClass, firstAvailableClass])

  const baggageLabel = intl.formatMessage({
    id: 'TtmcS1O5XRDvnNWVelJl',
    defaultMessage: 'Baggage',
  })
  const unavailableForDatesLabel = intl.formatMessage({
    id: 'zCiQOrhGcfF_PI9nQs_2',
    defaultMessage: 'Sold out for selected dates.',
  })

  const getCabinName = useCallback((fareClass: FlightsClassTypes) => {
    return getCharterFareCabinLabel(intl, fareClass)
  }, [intl])

  const rewardPreviewOnly = !hasLoyaltyAccount || !checkInDate || !checkOutDate
  const renderBundleFromPrice = useCallback((fareClass: FareClassWithReward) => (
    <MaldivesCharterFareClassModalBundleFromPrice
      offer={offer}
      checkInDate={checkInDate}
      checkOutDate={checkOutDate}
      occupancies={occupancies}
      fareClass={fareClass}
      nights={nights}
      luxPlusEnabled={luxPlusEnabled}
      rewardPreviewOnly={rewardPreviewOnly}
      feederOriginAirportCode={feederOriginAirportCode}
    />
  ), [offer, checkInDate, checkOutDate, occupancies, nights, luxPlusEnabled, rewardPreviewOnly, feederOriginAirportCode])

  const renderFareClassCard = (baseFareClass: FlightsClassTypes) => {
    const content = CHARTER_CABIN_CONTENT[baseFareClass]
    if (!content) return null

    const fareOptions = visibleFareClasses.filter(fareClass => toBaseFareClass(fareClass) === baseFareClass)
    if (fareOptions.length === 0) return null

    return (
      <FareClassCard
        key={baseFareClass}
        imageId={content.imageId}
        name={getCabinName(baseFareClass)}
        options={fareOptions.map((fareClass) => {
          const isReward = isRewardFareClass(fareClass)
          const disabled = unavailableFareClasses.has(fareClass)
          return {
            fareId: fareClass,
            subtitle: disabled ?
                <BodyText variant="small" colour="neutral-three">{unavailableForDatesLabel}</BodyText> :
                renderBundleFromPrice(fareClass),
            isSelected: localSelectedClass === fareClass,
            disabled,
            isRewardOption: isReward,
          }
        })}
        onSelect={onClassSelect}
      >
        <VerticalSpacer gap={16}>
          <VerticalSpacer gap={8}>
            <CharterCabinFeatureList fareClass={baseFareClass} section="inclusions" />
          </VerticalSpacer>
          <VerticalSpacer gap={8}>
            <BodyText variant="small" weight="bold">{baggageLabel}</BodyText>
            <CharterCabinFeatureList fareClass={baseFareClass} section="baggage" />
          </VerticalSpacer>
        </VerticalSpacer>
      </FareClassCard>
    )
  }

  const cards = baseFareClasses.map(renderFareClassCard)

  const rewardSeatsFilterPill = hasAnyRewardFareClass ? (
    <MaldivesCharterRewardSeatsToggle
      checked={rewardSeatsOnly}
      onChange={setRewardSeatsOnly}
    />
  ) : null

  const selectAClassTitle = intl.formatMessage({
    id: 'cQ1XgUVc5zOpGzYRq84Q',
    defaultMessage: 'Select a class',
  })
  const continueLabel = intl.formatMessage({
    id: '4tS92bLuwVUI3LcE957Q',
    defaultMessage: 'Continue',
  })
  const loginToViewLabel = intl.formatMessage({
    id: 'qeSf4C3WQDiM2egpxSR1',
    defaultMessage: 'Log in to view',
  })
  const joinSocieteLabel = intl.formatMessage({
    id: 'CV9ZQTxCiAPJb38-vKch',
    defaultMessage: 'Join {loyaltyProgramName}',
  }, { loyaltyProgramName: getLoyaltyProgramDisplayName() })
  const primaryActionText = lockedForLogin ? loginToViewLabel : lockedForEnroll ? joinSocieteLabel : continueLabel

  const modalTitle = rewardSeatsFilterPill && isDesktopUp
    ? (
        <Group direction="horizontal" horizontalAlign="space-between" gap={16} verticalAlign="center" fullWidth>
          <span style={{ minWidth: 0 }}>{selectAClassTitle}</span>
          {rewardSeatsFilterPill}
        </Group>
      )
    : selectAClassTitle

  return <Modal
    title={modalTitle}
    subtitle={intl.formatMessage({
      id: 'Xpek05yZHohlkLZhMZhI',
      defaultMessage: 'Maldivian · Airbus A330-200',
    })}
    headerExtension={rewardSeatsFilterPill && !isDesktopUp ? rewardSeatsFilterPill : undefined}
    primaryActionText={primaryActionText}
    onPrimaryActionClick={handlePrimaryActionClick}
    onClose={closeModal}
    size="XL"
    height="full"
    tabletHeight="auto"
  >
    <CarouselContainer>
      <CardCarousel
        gap={12}
        gutterSize={20}
        gutterStyle="overflow"
        snap="start"
        pageSize="auto"
        width={rem(272)}
        tabletWidth={rem(288)}
        desktopWidth={rem(300)}
      >
        {cards}
      </CardCarousel>
    </CarouselContainer>
  </Modal>
}

export default MaldivesCharterFlightFareClassModal
